/*
 *  -----------------------------------------------------------------------
 *    <copyright file="excel-listing-config.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { IActionItemV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from 'hooks';
import { ReportDefinitionFields } from 'queries/generated/fragments.generated';
import { GET_REPORT_DEFINITIONS } from 'queries/report-definition';
import Constants from 'utils/constants';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import AddEditCustomReport from 'views/components/reports/custom-report-config/add-edit-custom-report';
import CustomReportGrid from 'views/components/reports/custom-report-config/custom-report-grid';
import DeleteCustomReportDialog from 'views/components/reports/custom-report-config/delete-custom-report-config';

import './style.scss';

const AdminOrgExcelListingConfig = () => {
    const user = useAuthorization();
    const { state: localState } = useLocalState();
    const { t } = useTranslation();
    const canEdit = user.canWriteSystemAdminLevel(false, false, true);
    const openAddReportConfigDialog = useBoolean(false);
    const openDeleteReportConfigDialog = useBoolean(false);
    const [currentReport, setCurrentReport] = useState<ReportDefinitionFields | null>(null);
    const [reports, setReports] = useState([] as ReportDefinitionFields[]);
    const [loadReports, { data, loading, error }] = useLazyQuery(GET_REPORT_DEFINITIONS);

    const organisation = localState?.selectedStudy?.organisation || localState?.selectedOrganisation;

    const setReportsToOrgOnly = () => {
        const items = data.getReportDefinitions.filter(
            (item: ReportDefinitionFields) => item.userId === Constants.ObjectId.Empty,
        );

        setReports(items);
    };

    useEffect(() => {
        if (organisation) loadReports();
    }, [organisation]);

    useEffect(() => {
        if (data) setReportsToOrgOnly();
    }, [data]);

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const actions = (item: ReportDefinitionFields): IActionItemV2[] => [
        {
            label: 'admin:editConfig',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentReport(item);
                openAddReportConfigDialog.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'admin:deleteConfig',
            icon: ['fal', 'trash'],
            onClick: () => {
                setCurrentReport(item);
                openDeleteReportConfigDialog.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addCustomReport'),
            onClick: () => openAddReportConfigDialog.setTrue(),
            customClassName: 'grid-header__item--always-visible',
            icon: ['fal', 'plus'],
            disabled: !canEdit,
        },
    ];

    return (
        <>
            <AddEditCustomReport
                open={openAddReportConfigDialog.value}
                onClose={() => {
                    openAddReportConfigDialog.setFalse();
                    setCurrentReport(null);
                }}
                reportDefinition={currentReport ?? undefined}
                reportDefinitions={reports}
                isUserReport={false}
                canViewInternalRisks
            />
            <DeleteCustomReportDialog
                currentPanel={currentReport}
                open={openDeleteReportConfigDialog.value}
                reportId={currentReport?.id}
                onClose={() => {
                    openDeleteReportConfigDialog.setFalse();
                    setCurrentReport(null);
                }}
            />
            <PageTitle text={t('admin:manageExcelListingConfigurations')} className="mb-10" />
            <CustomReportGrid
                reports={reports}
                actions={actions}
                title={t('admin:organizationExcelListings')}
                extraMenuItems={extraMenuItems}
                className="manage-excel-listing-grid"
            />
        </>
    );
};

export default AdminOrgExcelListingConfig;
